import React, { Suspense, useEffect } from 'react';
import { Provider } from 'react-redux';
import { ToastContainer, Slide } from 'react-toastify';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { HMSRoomProvider, selectIsConnectedToRoom, useHMSActions, useHMSStore } from '@100mslive/react-sdk';
import { Box, globalStyles, HMSThemeProvider } from '@100mslive/roomkit-react';
import { AppData } from './components/AppData/AppData.jsx';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Init } from './components/init/Init';
import { KeyboardHandler } from './components/Input/KeyboardInputManager';
import { Notifications } from './components/Notifications';
import { ToastContainers } from './components/Toast/ToastContainer';
import store from './redux/store';
import { palette } from './theme.js';
import { RemoteStopScreenshare } from './plugins/RemoteStopScreenshare';
import { shadeColor } from './common/utils';
import { FeatureFlags } from './services/FeatureFlags';
import { generateUrl } from './utils/helper.jsx';
import RouteLists from './platform/components/RouteLists.jsx';
import InterviewPlatformView from './layouts/InterviewPlatformView.jsx';
import './assets/fonts/stylesheet.css';
import Loader from './platform/components/common/Loader.jsx';
const defaultTokenEndpoint = generateUrl('token_generation');
const envPolicyConfig = JSON.parse('{}');

const CloseButton = ({ closeToast }) => (
    <i className="fa fa-times-circle align-self-center f-18 text-white" onClick={closeToast} />
);

let appName;
if (window.location.host.includes('localhost')) {
    appName = 'localhost';
} else {
    appName = window.location.host.split('.')[0];
}

document.title = 'InCruiter - Mock Interview Platform' || `${appName}'s ${document.title}`;

// TODO: remove now that there are options to change to portrait
const getAspectRatio = ({ width, height }) => {
    const host = process.env.REACT_APP_HOST_NAME || window.location.hostname;
    const portraitDomains = (process.env.REACT_APP_PORTRAIT_MODE_DOMAINS || '').split(',');
    if (portraitDomains.includes(host) && width > height) {
        return { width: height, height: width };
    }
    return { width, height };
};

export function EdtechComponent({
    tokenEndpoint = defaultTokenEndpoint,
    themeConfig: {
        aspectRatio = '1-1',
        color = '#2F80FF',
        theme = 'dark',
        logo = '',
        headerPresent = 'false',
        metadata = '',
        recordingUrl = `${generateUrl('meeting')}/room/preview/beam`,
    },
    policyConfig = envPolicyConfig,
}) {
    const { 0: width, 1: height } = aspectRatio.split('-').map((el) => parseInt(el));
    globalStyles();
    return (
        <Suspense fallback={<Loader />}>
            <Provider store={store}>
                <ErrorBoundary>
                    <HMSThemeProvider
                        themeType={theme}
                        aspectRatio={getAspectRatio({ width, height })}
                        theme={{
                            colors: {
                                ...palette[theme],
                                primary_default: color,
                                primary_dim: shadeColor(color, -30),
                                primary_bright: shadeColor(color, 30),
                                primary_disabled: shadeColor(color, 10),
                            },
                        }}
                    >
                        <HMSRoomProvider isHMSStatsOn={FeatureFlags.enableStatsForNerds} leaveOnUnload={false}>
                            <AppData
                                appDetails={metadata}
                                policyConfig={policyConfig}
                                logo={logo}
                                recordingUrl={recordingUrl}
                                tokenEndpoint={tokenEndpoint}
                            />
                            <Init />
                            <Box
                                css={{
                                    bg: '#191924',
                                    w: '100%',
                                    lineHeight: '1.5',
                                    '-webkit-text-size-adjust': '100%',
                                    ...(headerPresent === 'true' ? { flex: '1 1 0', minHeight: 0 } : { h: '100%' }),
                                }}
                            >
                                <AppRoutes />
                            </Box>
                        </HMSRoomProvider>
                    </HMSThemeProvider>
                </ErrorBoundary>
            </Provider>
        </Suspense>
    );
}

const RouteList = () => {
    return (
        <Routes>
            <Route path="room">
                <Route path="meeting/:role/:roomId" element={<InterviewPlatformView component="Conference" />} />
                <Route path="preview/:role/:roomId" element={<InterviewPlatformView component="PreviewScreen" />} />
                <Route path="practice/:role/:roomId" element={<InterviewPlatformView component="PracticeRoom" />} />
                <Route
                    path="verify/:role/:roomId/:key"
                    element={<InterviewPlatformView component="InterviewStart" />}
                />
                <Route path="verify/:role/:roomId" element={<InterviewPlatformView component="InterviewStart" />} />
                <Route path="leave/:role/:roomId" element={<InterviewPlatformView component="PostLeave" />} />
                <Route
                    path="recording/:interviewId/:roomId"
                    element={<InterviewPlatformView component="RecordingRoom" />}
                />
                <Route
                    path="feedback/:interviewId/:roomId"
                    element={<InterviewPlatformView component="FeedbackRoom" />}
                />
                <Route
                    path="recording/:interviewId/:roomId/:key"
                    element={<InterviewPlatformView component="RecordingRoom" />}
                />
                <Route
                    path="feedback/:interviewId/:roomId/:key"
                    element={<InterviewPlatformView component="FeedbackRoom" />}
                />
            </Route>
            <Route path="/*" element={<RouteLists />} />
        </Routes>
    );
};

const BackSwipe = () => {
    const isConnectedToRoom = useHMSStore(selectIsConnectedToRoom);
    const hmsActions = useHMSActions();
    useEffect(() => {
        const onRouteLeave = async () => {
            if (isConnectedToRoom) {
                await hmsActions.leave();
            }
        };
        window.addEventListener('popstate', onRouteLeave);
        return () => {
            window.removeEventListener('popstate', onRouteLeave);
        };
    }, [hmsActions, isConnectedToRoom]);
    return null;
};

function AppRoutes() {
    return (
        <Router>
            <ToastContainer
                autoClose={2000}
                draggable={false}
                transition={Slide}
                closeButton={CloseButton}
                hideProgressBar={true}
                toastClassName="toast-notification"
            />
            <ToastContainers />
            <Notifications />
            <BackSwipe />
            <RemoteStopScreenshare />
            <KeyboardHandler />
            <Routes>
                <Route path="/*" element={<RouteList />} />
            </Routes>
        </Router>
    );
}

export default function App() {
    return (
        <EdtechComponent
            themeConfig={{
                aspectRatio: '1-1',
                theme: 'dark',
                color: '#2F80FF',
                logo: '',
                headerPresent: process.env.REACT_APP_HEADER_PRESENT,
                metadata: '{}', // A stringified object in env
            }}
        />
    );
}
